import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { videoService, type VideoWithProvider } from '../../services'
import { useVideoStore } from '../../stores'
import { VideoGrid, VideoFilters, Pagination } from '../../components/video'
import { Input } from '../../components/common'
import { muxConfig } from '../../config/mux.config'

// Category data with icons and colors
const CATEGORIES = [
  { name: 'Strength', icon: '💪', color: 'bg-red-100 text-red-600 border-red-200' },
  { name: 'Stretching', icon: '🧘', color: 'bg-purple-100 text-purple-600 border-purple-200' },
  { name: 'Cardio', icon: '❤️', color: 'bg-pink-100 text-pink-600 border-pink-200' },
  { name: 'Mobility', icon: '🔄', color: 'bg-blue-100 text-blue-600 border-blue-200' },
  { name: 'Balance', icon: '⚖️', color: 'bg-green-100 text-green-600 border-green-200' },
  { name: 'Recovery', icon: '🌿', color: 'bg-teal-100 text-teal-600 border-teal-200' },
]

export function VideoBrowsePage() {
  const { filters, pagination, setTotal, setPage, setFilter, resetFilters } = useVideoStore()
  const [videos, setVideos] = useState<VideoWithProvider[]>([])
  const [popularVideos, setPopularVideos] = useState<VideoWithProvider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [categories, setCategories] = useState<string[]>([])

  // Load categories
  useEffect(() => {
    videoService.getCategories().then(setCategories).catch(console.error)
  }, [])

  // Load popular videos on mount
  useEffect(() => {
    const loadPopular = async () => {
      try {
        const { videos } = await videoService.getPublicVideos(
          { search: '', category: null, tags: [], sortBy: 'popular', visibility: 'all' },
          { page: 1, pageSize: 4, total: 0 }
        )
        setPopularVideos(videos)
      } catch (error) {
        console.error('Failed to load popular videos:', error)
      }
    }
    loadPopular()
  }, [])

  // Load filtered videos
  useEffect(() => {
    const loadVideos = async () => {
      setIsLoading(true)
      try {
        const { videos, total } = await videoService.getPublicVideos(
          filters,
          pagination
        )
        setVideos(videos)
        setTotal(total)
      } catch (error) {
        console.error('Failed to load videos:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadVideos()
  }, [filters, pagination.page, pagination.pageSize, setTotal])

  // Handle hero search
  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter('search', searchInput)
    setShowFilters(true)
  }

  // Handle category click
  const handleCategoryClick = (category: string) => {
    resetFilters()
    setFilter('category', category)
    setShowFilters(true)
  }

  const totalPages = Math.ceil(pagination.total / pagination.pageSize)
  const hasActiveFilters = filters.search || filters.category || filters.tags.length > 0

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              Discover Exercise Videos
            </h1>
            <p className="mt-4 text-lg text-blue-100">
              Browse our library of professional exercise videos from certified trainers and therapists
            </p>

            {/* Hero Search */}
            <form onSubmit={handleHeroSearch} className="mt-8 max-w-xl mx-auto">
              <div className="relative">
                <Input
                  placeholder="Search for exercises, body parts, or techniques..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-gray-900 text-lg rounded-xl border-0 shadow-lg"
                />
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Browse by Category</h2>
            {categories.length > 6 && (
              <button className="text-sm text-blue-600 hover:text-blue-800">
                View all categories
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {CATEGORIES.map((cat) => {
              const isAvailable = categories.includes(cat.name)
              return (
                <button
                  key={cat.name}
                  onClick={() => isAvailable && handleCategoryClick(cat.name)}
                  disabled={!isAvailable}
                  className={`flex flex-col items-center p-4 rounded-xl border transition-all ${
                    isAvailable
                      ? `${cat.color} hover:shadow-md cursor-pointer`
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                  }`}
                >
                  <span className="text-2xl mb-1">{cat.icon}</span>
                  <span className="text-sm font-medium">{cat.name}</span>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Popular Videos (only show if no active filters) */}
      {!hasActiveFilters && popularVideos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-b border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Popular Right Now</h2>
              <p className="text-sm text-gray-600">Most viewed exercises this week</p>
            </div>
            <button
              onClick={() => {
                resetFilters()
                setFilter('sortBy', 'popular')
                setShowFilters(true)
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              View all popular &rarr;
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularVideos.map((video) => (
              <Link
                key={video.id}
                to={`/videos/${video.id}`}
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-video bg-gray-200 relative">
                  {video.mux_playback_id ? (
                    <img
                      src={muxConfig.getThumbnailUrl(video.mux_playback_id, {
                        width: 640,
                        height: 360,
                        time: video.thumbnail_time ?? (video.duration_seconds ? video.duration_seconds * 0.25 : 0),
                      })}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  {video.duration_seconds && (
                    <span className="absolute bottom-2 right-2 bg-black/75 text-white text-xs px-2 py-1 rounded">
                      {Math.floor(video.duration_seconds / 60)}:{String(video.duration_seconds % 60).padStart(2, '0')}
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                    {video.title}
                  </h3>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                    <span>{video.user?.full_name || 'Unknown Provider'}</span>
                    <span>•</span>
                    <span>{video.view_count || 0} views</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Toggle & Active Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-gray-900">
              {hasActiveFilters ? 'Search Results' : 'All Videos'}
            </h2>
            {hasActiveFilters && (
              <div className="flex items-center gap-2">
                {filters.search && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    "{filters.search}"
                    <button
                      onClick={() => setFilter('search', '')}
                      className="hover:text-blue-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.category && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                    {filters.category}
                    <button
                      onClick={() => setFilter('category', null)}
                      className="hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    resetFilters()
                    setSearchInput('')
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {(filters.tags.length > 0 || filters.sortBy !== 'newest') && (
              <span className="w-2 h-2 bg-blue-600 rounded-full" />
            )}
          </button>
        </div>

        {/* Collapsible Filters */}
        {showFilters && (
          <div className="mb-8">
            <VideoFilters />
          </div>
        )}

        {/* Results count */}
        <div className="mb-4 text-sm text-gray-600">
          {pagination.total > 0 ? (
            <span>
              Showing {(pagination.page - 1) * pagination.pageSize + 1} -{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)}{' '}
              of {pagination.total} videos
            </span>
          ) : (
            !isLoading && <span>No videos found</span>
          )}
        </div>

        {/* Video grid */}
        <VideoGrid
          videos={videos}
          isLoading={isLoading}
          emptyMessage="No videos match your filters. Try adjusting your search or browse by category above."
        />

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* CTA Section */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">
            Are you a fitness professional?
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
            Upload your own exercise videos, create custom programs, and share them with your clients.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Create Free Account
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors border border-gray-700"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
